import { useEffect, useRef, useState } from "react";
import "./App.css";

const EXPERIENCE = [
  {
    date: "Jan 2026 — Present",
    company: "Prairie Robotics Inc.",
    role: "Software Development Intern",
    points: [
      "Built and maintained internal data systems through entry, cleaning, and validation.",
      "Wrote scripts to automate data checks and improve reliability of operational datasets.",
      "Supported internal reporting by analyzing datasets and identifying inconsistencies.",
      "Documented system behavior, workflows, and data handling procedures.",
    ],
  },
  {
    date: "Jan 2026 — Present",
    company: "University of Regina",
    role: "League Coordinator-Intramural Sports",
    points: [
      "Coordinated and managed intramural league operations, including scheduling, team registrations, and game-day supervision.",
      "Led league logistics using IMLeagues, maintaining accurate standings, scores, and participant records.",
      "Enforced rules, resolved conflicts professionally, and ensured a safe, inclusive playing environment.",
      "Supervised referees and volunteers while promoting sportsmanship and competitive integrity.",
    ],
  },
  {
    date: "Sep 2023 — Present",
    company: "CSSS, U of R",
    role: "Financial Officer",
    points: [
      "Maintained financial datasets using Excel and SQL for analysis and reporting.",
      "Automated recurring reports, reducing manual processing time by 40%.",
      "Cleaned and reconciled datasets to ensure accuracy across reporting periods.",
      "Documented reporting processes and data structures for team continuity.",
    ],
  },
  {
    date: "May 2023 — Aug 2024",
    company: "Government of Saskatchewan",
    role: "Geographic Names Assistant",
    points: [
      "Organized, cleaned, and verified large datasets across multiple databases.",
      "Performed data quality checks and maintained standardized data structures.",
      "Assisted with documentation of processes and system updates.",
      "Supported ad hoc data requests and analysis tasks across departments.",
    ],
  },
  
];

const PROJECTS = [
  { num: "01", name: "Sterling", desc: "Quantitative equity research pipeline — survivorship-free market data in DuckDB, gradient-boosted return models, and walk-forward validation with an embargo gap. Built to detect false signals, not to flatter them.", tags: ["Python", "Machine Learning", "DuckDB"], url: "https://siddharth2205.github.io/sterling-agent/" },
  { num: "02", name: "LetsGoFishing", desc: "A full fishing game built from scratch with OpenGL — game loops, real-time rendering, collision detection, and interactive mechanics.", tags: ["OpenGL", "C++", "Game Dev"], url: "https://github.com/Siddharth2205/LetsGoFishing" },
  { num: "03", name: "Home Services Platform", desc: "Full-stack web platform for booking home services — service listings, user auth, and appointment scheduling end-to-end.", tags: ["PHP", "MySQL", "Full Stack"], url: "http://www.webdev.cs.uregina.ca/~smx993/CS476-2/all-in-one-home-services/index.php" },
  { num: "04", name: "Tisha Library", desc: "A private digital library for collecting books, saving highlights, journaling notes, uploading photos, and sketching — with auth, relational tables, and file storage behind it.", tags: ["React", "Supabase", "Full Stack"], url: "https://tisha-library.vercel.app/" },
  { num: "05", name: "Trivia AI", desc: "AI-powered trivia game with dynamic question generation and answers.", tags: ["Python", "API", "Data"], url: "https://trivia-700nv3haw-siddharth2205s-projects.vercel.app/" },
  { num: "06", name: "Bus Alert", desc: "App to notify users of destination arrival using live data from regina transit.", tags: ["TypeScript", "Live Data", "Transit API"], url: "https://bus-alert.vercel.app/" },
  { num: "07", name: "Mood Recommender", desc: "Pick your current mood and get activity suggestions to match — a small, fast frontend built around a simple recommendation flow.", tags: ["JavaScript", "Frontend", "Vercel"], url: "https://activity-recommander.vercel.app/" },
  { num: "08", name: "Lyrical", desc: "A music-themed frontend web project with clean layout, structured content, and thoughtful typographic hierarchy.", tags: ["HTML", "CSS", "Frontend"], url: "https://lyrical-kqii.vercel.app/" },
  { num: "09", name: "Roll The Dice", desc: "CSS-driven dice rolling web app with smooth animations and interactivity built with vanilla web technologies.", tags: ["CSS", "JavaScript", "Animation"], url: "https://github.com/Siddharth2205/RollTheDice" },
];

const SKILLS = {
  Languages: ["Python", "JavaScript", "TypeScript", "SQL", "PHP", "C++", "HTML / CSS"],
  "Data & Analytics": ["Data Cleaning", "Data Validation", "Pandas / scikit-learn", "Automation Scripts", "Excel (Advanced)", "Dashboards", "Relational DBs"],
  "Tools & Platforms": ["Git / GitHub", "React", "Supabase", "DuckDB", "OpenGL", "Linux", "MySQL / PostgreSQL"],
  Concepts: ["Data Quality", "Feature Engineering", "Process Improvement", "Documentation", "Software Engineering", "IT Strategy"],
};

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.unobserve(el); }
    }, { threshold: 0.08 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

function SectionRule({ num, name }) {
  return (
    <div className="section-rule">
      <span className="section-num">{num}</span>
      <div className="section-line" />
      <span className="section-name">{name}</span>
    </div>
  );
}

function RevealBox({ children, className = "", direction = "up", delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`reveal reveal-${direction} ${visible ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Cursor() {
  const curRef = useRef(null);
  const trailRef = useRef(null);
  const pos = useRef({ mx: 0, my: 0, tx: 0, ty: 0 });
  useEffect(() => {
    const onMove = (e) => {
      pos.current.mx = e.clientX; pos.current.my = e.clientY;
      if (curRef.current) curRef.current.style.transform = `translate(${e.clientX - 5}px,${e.clientY - 5}px)`;
    };
    document.addEventListener("mousemove", onMove);
    let raf;
    const animate = () => {
      pos.current.tx += (pos.current.mx - pos.current.tx) * 0.14;
      pos.current.ty += (pos.current.my - pos.current.ty) * 0.14;
      if (trailRef.current) trailRef.current.style.transform = `translate(${pos.current.tx - 16}px,${pos.current.ty - 16}px)`;
      raf = requestAnimationFrame(animate);
    };
    animate();
    return () => { document.removeEventListener("mousemove", onMove); cancelAnimationFrame(raf); };
  }, []);
  return (<><div ref={curRef} className="cursor-dot" /><div ref={trailRef} className="cursor-trail" /></>);
}

function Nav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handler = () => {
      let cur = "";
      ["hero","about","experience","projects","skills","contact"].forEach((id) => {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 90) cur = id;
      });
      setActive(cur);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <nav className="nav">
      <div className="nav-left">
        <button className="nav-logo" onClick={() => scrollTo("hero")}>Siddharth Modi</button>
        <span className="nav-issue">Portfolio · 2025</span>
      </div>
      <ul className="nav-links">
        {["about","experience","projects","skills","contact"].map((id) => (
          <li key={id}><button className={`nav-link ${active === id ? "nav-active" : ""}`} onClick={() => scrollTo(id)}>{id.charAt(0).toUpperCase() + id.slice(1)}</button></li>
        ))}
      </ul>
      <button className="nav-hire" onClick={() => scrollTo("contact")}>Hire Me</button>
    </nav>
  );
}

function Hero() {
  return (
    <section id="hero" className="hero">
      <div className="hero-main">
        <div className="hero-left">
          <div className="hero-kicker">Software Developer &amp; CS Student</div>
          <h1 className="hero-title">Building<br />Things<br /><em>That Work.</em></h1>
          <p className="hero-tagline">Data systems, automation tools, and web applications — built with care and shipped with confidence.</p>
          <div className="hero-actions">
            <button className="btn-red" onClick={() => document.getElementById("projects").scrollIntoView({ behavior: "smooth" })}>View Projects</button>
            <button className="btn-outline" onClick={() => document.getElementById("contact").scrollIntoView({ behavior: "smooth" })}>Get In Touch</button>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-stat"><div className="hero-stat-label">Currently</div><div className="hero-stat-company">Software Dev Intern<br /><span>Prairie Robotics Inc.</span></div></div>
          <div className="hero-divider" />
          <div className="hero-stat"><div className="hero-stat-label">Graduating</div><div className="hero-stat-val">Dec <em>'26</em></div></div>
          <div className="hero-divider" />
          <div className="hero-stat"><div className="hero-stat-label">GitHub Repos</div><div className="hero-stat-val">11<em>+</em></div></div>
          <div className="hero-divider" />
          <div className="hero-stat"><div className="hero-stat-label">Location</div><div className="hero-stat-loc">Regina, Saskatchewan<br />Canada</div></div>
        </div>
      </div>
      <div className="hero-bar">
        <span className="hero-bar-item">Python · SQL · JavaScript · PHP · C++</span>
        <span className="hero-bar-item">University of Regina</span>
        <span className="hero-bar-item"><a href="https://github.com/Siddharth2205" target="_blank" rel="noreferrer">github.com/Siddharth2205 ↗</a></span>
        <span className="hero-bar-item"><a href="https://linkedin.com/in/siddharth-modi-095356194" target="_blank" rel="noreferrer">LinkedIn ↗</a></span>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="section-border">
      <SectionRule num="01" name="About" />
      <div className="about-inner">
        <RevealBox className="about-col-left" direction="left">
          <h2 className="about-headline">A developer who <em>actually</em> reads the docs.</h2>
          <div className="about-body">
            <p>I'm a fourth-year CS student at the University of Regina who enjoys working across the full stack — from Python scripts that untangle messy datasets to web apps and OpenGL games from scratch.</p>
            <p>Currently interning at Prairie Robotics Inc., building and maintaining internal data systems, automating validation workflows, and making sure the data engineers rely on is actually trustworthy.</p>
            <p>I also serve as Financial Officer for the CS Students' Society, where I automated reporting that cut manual processing time.</p>
            <p>I care about writing code that's clean, well-documented, and maintainable by someone who isn't me at 2am six months later.</p>
          </div>
        </RevealBox>
        <RevealBox className="about-col-right" delay={100}>
          <div className="info-block">
            <div className="info-title">Education</div>
            {[["Degree","B.Sc. Computer Science"],["Institution","University of Regina"],["Expected","Dec 2026"],["Courses","DB Systems, Data Analytics, SE"]].map(([k,v]) => (
              <div className="info-row" key={k}><span className="info-key">{k}</span><span className="info-val">{v}</span></div>
            ))}
          </div>
          <div className="info-block">
            <div className="info-title">Contact & Links</div>
            <div className="info-row"><span className="info-key">Email</span><span className="info-val"><a href="mailto:sidinregina@gmail.com">sidinregina@gmail.com</a></span></div>
            <div className="info-row"><span className="info-key">Phone</span><span className="info-val">+1 (639) 997-8144</span></div>
            <div className="info-row"><span className="info-key">GitHub</span><span className="info-val"><a href="https://github.com/Siddharth2205" target="_blank" rel="noreferrer">Siddharth2205 ↗</a></span></div>
            <div className="info-row"><span className="info-key">LinkedIn</span><span className="info-val"><a href="https://linkedin.com/in/siddharth-modi-095356194" target="_blank" rel="noreferrer">siddharth-modi ↗</a></span></div>
          </div>
        </RevealBox>
      </div>
    </section>
  );
}

function Experience() {
  return (
    <section id="experience" className="section-border">
      <SectionRule num="02" name="Experience" />
      <div className="exp-inner">
        {EXPERIENCE.map((e, i) => (
          <RevealBox key={i} className="exp-row" delay={i * 80}>
            <div className="exp-meta">
              <div className="exp-date">{e.date}</div>
              <div className="exp-company-name">{e.company}</div>
            </div>
            <div className="exp-body">
              <div className="exp-title">{e.role}</div>
              <ul className="exp-pts">{e.points.map((p, j) => <li key={j}>{p}</li>)}</ul>
            </div>
          </RevealBox>
        ))}
      </div>
    </section>
  );
}

function Projects() {
  return (
    <section id="projects" className="section-border">
      <SectionRule num="03" name="Projects" />
      <div className="projects-grid">
        {PROJECTS.map((p, i) => (
          <RevealBox key={i} delay={i * 60}>
            <a href={p.url} target="_blank" rel="noreferrer" className="proj-card">
              <div className="proj-num">{p.num}</div>
              <div className="proj-name">{p.name}</div>
              <div className="proj-desc">{p.desc}</div>
              <div className="proj-tags">{p.tags.map(t => <span className="proj-tag" key={t}>{t}</span>)}</div>
              <div className="proj-link">{p.url.includes("github.com") ? "View on GitHub" : "View Live Site"} <span className="arr">↗</span></div>
            </a>
          </RevealBox>
        ))}
      </div>
    </section>
  );
}

function Skills() {
  return (
    <section id="skills" className="section-border">
      <SectionRule num="04" name="Skills" />
      <div className="skills-inner">
        <div className="skills-top">
          <RevealBox direction="left"><h2 className="skills-intro">The tools I reach<br />for <em>first.</em></h2></RevealBox>
          <RevealBox delay={80}><p className="skills-body">From Python automation scripts to relational databases, cleaning messy real-world data, and shipping functional web applications — I've used these in production and in projects that actually got finished.</p></RevealBox>
        </div>
        <RevealBox className="skills-cats">
          {Object.entries(SKILLS).map(([cat, items]) => (
            <div key={cat}>
              <div className="skill-cat-title">{cat}</div>
              <ul className="skill-list">{items.map(s => <li key={s}>{s}</li>)}</ul>
            </div>
          ))}
        </RevealBox>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact">
      <div className="section-rule" style={{ background: "var(--warm)" }}>
        <span className="section-num">05</span>
        <div className="section-line" />
        <span className="section-name">Contact</span>
      </div>
      <div className="contact-inner">
        <RevealBox className="contact-left" direction="left">
          <div>
            <h2 className="contact-heading">Let's build<br />something <em>great.</em></h2>
            <p className="contact-sub">Open to internships, co-op roles, freelance projects, and interesting conversations. If you have something in mind, I'd love to hear about it.</p>
          </div>
          <div className="contact-email-block">
            <div className="contact-email-label">Send a message</div>
            <a href="mailto:sidinregina@gmail.com" className="contact-email-addr">sidinregina@gmail.com</a>
          </div>
          <div className="contact-phone">📞 &nbsp;+1 (639) 997-8144</div>
        </RevealBox>
        <RevealBox className="contact-right" delay={100}>
          <div>
            <div className="contact-links-title">Find me online</div>
            {[{ label: "GitHub", url: "https://github.com/Siddharth2205" },{ label: "LinkedIn", url: "https://linkedin.com/in/siddharth-modi-095356194" }].map(({ label, url }) => (
              <a key={label} href={url} target="_blank" rel="noreferrer" className="contact-link-row">
                <span className="contact-link-name">{label}</span>
                <span className="contact-link-arr">↗</span>
              </a>
            ))}
          </div>
          <div className="contact-footer-note">Based in Regina, Saskatchewan, Canada.<br />Available for remote and local opportunities.</div>
        </RevealBox>
      </div>
    </section>
  );
}

export default function App() {
  return (
    <>
      <Cursor />
      <Nav />
      <Hero />
      <About />
      <Experience />
      <Projects />
      <Skills />
      <Contact />
      <footer className="footer">
        <span>© 2025 Siddharth Modi</span>
        <span>Built with React &amp; care.</span>
        <a href="https://github.com/Siddharth2205" target="_blank" rel="noreferrer">github.com/Siddharth2205</a>
      </footer>
    </>
  );
}